import type {
    AppModule,
    AppSubmodule,
    ExposedMenu,
    MenuGroup,
    SideLink,
} from '@moniebook-web/components/widgets/sidebar';
import { FileChartLine } from '@moniebook-web/icons/document/file-chart-line';
import { BankCardLine } from '@moniebook-web/icons/finance/bank-card-line';
import { CoinLine } from '@moniebook-web/icons/finance/coin-line';
import { DiscountPercentLine } from '@moniebook-web/icons/finance/discount-percent-line';
import { GiftLine } from '@moniebook-web/icons/finance/gift-line';
import { PriceTagLine } from '@moniebook-web/icons/finance/price-tag-line';
import { ShoppingBasketLine } from '@moniebook-web/icons/finance/shopping-basket-line';
import { Dice4Line } from '@moniebook-web/icons/game-sports/dice-4-line';
import { Reports } from '@moniebook-web/icons/sidebar-rail/reports';
import { GroupLine } from '@moniebook-web/icons/user-faces/group-line';
import { User3Line } from '@moniebook-web/icons/user-faces/user-3-line';
import { UserLine } from '@moniebook-web/icons/user-faces/user-line';
import {
    ApplicationModule,
    MENU_PARENTS,
    MENU_SECTIONS,
} from '@moniebook-web/types';

const REPORTS_BASE_FILTER_KEYS = [
    'dateFrom',
    'dateTo',
    'timeFrom',
    'timeTo',
    'staff',
    'branches',
    'chartType',
    'groupBy',
    'saleType',
    'staffId',
    'register',
];

const REPORTS_PERMISSIONS = ['VIEW_REPORT'];

export const reportsMenu: ExposedMenu = {
    menuGroups: [
        {
            id: MENU_PARENTS.REPORTS,
            name: 'Reports',
            sectionId: MENU_SECTIONS.NONE,
            order: 4,
            icon: <FileChartLine />,
        },
    ] as MenuGroup[],
    submenu: [
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 1,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales summary',
            to: '/dashboard/reports/summary',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <PriceTagLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 2,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by item',
            to: '/dashboard/reports/sales-by-item',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <ShoppingBasketLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 3,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Modifiers',
            to: '/dashboard/reports/modifiers',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            featureFlag: 'moniebook.pos.modifierReportsEnabled',
            icon: <GiftLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 4,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by category',
            to: '/dashboard/reports/sales-by-category',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <Dice4Line />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 5,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Loyalty',
            to: '/dashboard/reports/loyalty',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            featureFlag: 'moniebook.pos.loyaltyReportsEnabled',
            icon: <GiftLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 6,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by staff',
            to: '/dashboard/reports/staff',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <GroupLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 7,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by customer',
            to: '/dashboard/reports/customer',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <User3Line />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 8,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by pricing groups',
            to: '/dashboard/reports/pricing-group',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            featureFlag: 'moniebook.pos.pricingGroups',
            icon: <PriceTagLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 9,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Payment method',
            to: '/dashboard/reports/payment-method',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <BankCardLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 10,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Discounts',
            to: '/dashboard/reports/discounts',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <DiscountPercentLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 11,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Taxes',
            to: '/dashboard/reports/tax',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <CoinLine />,
        },
    ] as SideLink[],
    modules: [
        {
            id: ApplicationModule.REPORTS,
            name: 'Reports',
            order: 3,
            icon: <Reports />,
            backgroundColor: '#FCE7F3',
            foregroundColor: '#DB2777',
        },
    ] as AppModule[],
    subModules: [
        {
            parentId: ApplicationModule.REPORTS,
            order: 1,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales summary',
            to: '/dashboard/reports/summary',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <FileChartLine />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 2,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by item',
            to: '/dashboard/reports/sales-by-item',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <ShoppingBasketLine />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 3,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Modifiers',
            to: '/dashboard/reports/modifiers',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            featureFlag: 'moniebook.pos.modifierReportsEnabled',
            icon: <ShoppingBasketLine />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 4,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by category',
            to: '/dashboard/reports/sales-by-category',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <Dice4Line />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 5,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Loyalty',
            to: '/dashboard/reports/loyalty',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            featureFlag: 'moniebook.pos.loyaltyReportsEnabled',
            icon: <GiftLine />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 6,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by staff',
            to: '/dashboard/reports/staff',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <UserLine />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 7,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Sales by customer',
            to: '/dashboard/reports/customer',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <User3Line />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 8,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Pricing group sales',
            to: '/dashboard/reports/pricing-group',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            featureFlag: 'moniebook.pos.pricingGroups',
            icon: <GroupLine />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 9,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Payment method',
            to: '/dashboard/reports/payment-method',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <BankCardLine />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 10,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Discounts',
            to: '/dashboard/reports/discounts',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <DiscountPercentLine />,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 11,
            accessCodeList: REPORTS_PERMISSIONS,
            name: 'Taxes',
            to: '/dashboard/reports/tax',
            keepSearchParams: true,
            keepSearchList: REPORTS_BASE_FILTER_KEYS,
            icon: <CoinLine />,
        },
    ] as AppSubmodule[],
};
